// 多语言配置
import { deepToTraditional, toTraditional } from './s2t'
import { ZH_TW_OVERRIDES } from './zh-TW-overrides'

export type Language = 'en' | 'zh' | 'zh-TW' | 'ja'

export const SUPPORTED_LANGUAGES: Language[] = ['en', 'zh', 'zh-TW', 'ja']

export interface Translations {
  // 通用
  common: {
    loading: string
    confirm: string
    cancel: string
    submit: string
    save: string
    delete: string
    edit: string
    back: string
    next: string
    previous: string
    search: string
    filter: string
    sort: string
    refresh: string
    close: string
    open: string
    yes: string
    no: string
    all: string
    noData: string
    completed: string
    pending: string
    failed: string
    retry: string
    operationSuccess: string
    operationFailed: string
    networkError: string
    deleteSuccess: string
  }
  navigation: {
    home: string
    market: string
    trade: string
    portfolio: string
    mine: string
    wallet: string
  }
  
  // 首页
  home: {
    totalAssets: string
    available: string
    trading: string
    marketData: string
    wallet: string
    marketOverview: string
    secure: string
    highLiquidity: string
    quickDeposit: string
    financialNews: string
  }

  // 财经新闻
  news: {
    title: string
    viewAll: string
    noNews: string
    source: string
    loadFailed: string
    categoryMarket: string
    categoryMacro: string
    categoryFutures: string
    categoryCrypto: string
  }
  
  // 交易
  trading: {
    trade: string
    spotTrading: string
    marginTrading: string
    secondsTrading: string
    buy: string
    sell: string
    long: string
    short: string
    market: string
    limit: string
    price: string
    quantity: string
    amount: string
    available: string
    leverage: string
    closePrice: string
    lots: string
    percentage: string
    marketValue: string
    margin: string
    frozen: string
    pending: string
    current: string
    history: string
    cancelled: string
    holding: string
    closed: string
    liquidated: string
    noHolding: string
    noClosedRecords: string
    noCancelledRecords: string
    openPrice: string
    profit: string
    usStock: string
    hkStock: string
    crypto: string
    volume24h: string
    high24h: string
    low24h: string
    closePosition: string
    closePositionConfirm: string
    closePositionSuccess: string
    closePositionFailed: string
    liquidatedOnClose: string
    unrealizedProfit: string
    positionStatus: string
    openPositionLabel: string
    crossMargin: string
    positionQty: string
    execClosePrice: string
    tpSl: string
    notSet: string
    statusLabel: string
  }
  
  // 市场
  market: {
    market: string
    asset: string
    price: string
    change: string
    volume: string
    marketCap: string
    priceChange: string
    searchPlaceholder: string
    loadFailed: string
    tabCrypto: string
    tabFutures: string
    tabUsStock: string
    tabHkStock: string
  }

  // 国际期货品种名称
  futures: {
    GC: string
    CL: string
    BZ: string
    SI: string
    HG: string
    ES: string
    NQ: string
    YM: string
    RTY: string
    NG: string
    '6E': string
    '6J': string
    '6B': string
    ZN: string
    ZB: string
  }
  
  // 我的页面
  profile: {
    profile: string
    walletManagement: string
    accountRecords: string
    securityCenter: string
    aboutUs: string
    logout: string
    recentActivity: string
    deposit: string
    withdraw: string
    securityLevel: string
    verified: string
    uid: string
    walletDesc: string
    recordsDesc: string
    securityDesc: string
    aboutDesc: string
    logoutDesc: string
    logoutConfirm: string
    confirmLogout: string
    noActivity: string
    securityLevelHigh: string
  }
  security: {
    emailBinding: string
    notBound: string
    loginPassword: string
    changePassword: string
    currentPassword: string
    newPassword: string
    confirmNewPassword: string
    enterCurrentPassword: string
    enterNewPassword: string
    enterConfirmPassword: string
    passwordMismatch: string
    passwordMinLength: string
    passwordChanged: string
    passwordChangeFailed: string
  }
  
  // 关于我们
  about: {
    contactUs: string
    noContent: string
  }
  
  // 记录
  records: {
    withdrawApproved: string
    withdrawRejected: string
    address: string
    marginClose: string
    marginLiquidated: string
    margin: string
    profit: string
    lostMargin: string
    adminDeposit: string
  }
  
  // 认证
  auth: {
    login: string
    logout: string
    email: string
    password: string
    loginSuccess: string
    loginFailed: string
    pleaseLogin: string
    sessionExpired: string
  }
  
  // 客服
  customerService: {
    title: string
    online: string
    offline: string
    welcomeMessage: string
    autoReply: string
    inputPlaceholder: string
    connecting: string
    sendMessage: string
    sendHint: string
    initFailed: string
    connectFailed: string
    sendFailed: string
    sendImageSuccess: string
    sendImageFailed: string
    supportAgent: string
    userLabel: string
    generalInquiry: string
  }
  
  // 时间和日期
  time: {
    minute: string
    hour: string
    day: string
    week: string
    month: string
    year: string
  }
  
  // KYC认证
  kyc: {
    title: string
    approved: string
    approvedDesc: string
    reviewing: string
    reviewingDesc: string
    reviewingTime: string
    rejected: string
    rejectedReason: string
    resubmit: string
    realName: string
    realNamePlaceholder: string
    idNumber: string
    idNumberPlaceholder: string
    idFront: string
    idBack: string
    uploadTip: string
    submitTip: string
    submitTime: string
    refreshStatus: string
    fillRequired: string
    uploadRequired: string
    submitSuccess: string
    submitFailed: string
    networkError: string
    notVerified: string
    clickToVerify: string
    waitingReview: string
    rejectedClickResubmit: string
  }
  
  // 钱包/资产
  wallet: {
    withdrawAmount: string
    withdrawAmountPlaceholder: string
    withdrawAddress: string
    withdrawAddressPlaceholder: string
    fee: string
    actualAmount: string
    withdrawing: string
    withdrawSuccess: string
  }

  deposit: {
    title: string
    instructions: string
    network: string
    address: string
    copyAddress: string
    addressCopied: string
    scanQr: string
    amount: string
    amountPlaceholder: string
    minAmount: string
    screenshot: string
    uploadScreenshot: string
    uploadHint: string
    submit: string
    submitSuccess: string
    submitFailed: string
    noAddress: string
    pending: string
    approved: string
    rejected: string
    history: string
    selectScreenshot: string
  }
  
  // 图表
  chart: {
    noData: string
    highest: string
    lowest: string
    current: string
    priceChart: string
    loadFailed: string
    klineChart: string
    dataPoints: string
    recentData: string
    open: string
    close: string
    changeCol: string
    retry: string
    time: string
    interval1m: string
    interval5m: string
    interval15m: string
    interval1h: string
    interval4h: string
    interval1d: string
  }
  
  // 订单
  order: {
    noOrders: string
    quantity: string
  }

  // 秒合约
  seconds: {
    title: string
    direction: string
    bullish: string
    bearish: string
    duration: string
    investAmount: string
    currentPrice: string
    profitRate: string
    expectedProfit: string
    availableBalance: string
    confirmBullish: string
    confirmBearish: string
    orderSubmitted: string
    insufficientBalance: string
    orderHistory: string
    inProgress: string
    settled: string
    win: string
    lose: string
    countdown: string
    openPrice: string
    closePrice: string
    profit: string
    noOrders: string
    second: string
    minute: string
  }
  
  // 价格提醒
  priceAlert: {
    deleteConfirm: string
    selectAsset: string
    selectAssetPlaceholder: string
    condition: string
    conditionPlaceholder: string
    above: string
    below: string
    targetPrice: string
    targetPricePlaceholder: string
    notificationMethod: string
    notificationPlaceholder: string
    emailNotify: string
    smsNotify: string
    pushNotify: string
    enableStatus: string
    pageTitle: string
    createAlert: string
    totalCount: string
    activeCount: string
    triggeredCount: string
    recordsTotal: string
    triggeredTag: string
    createdAt: string
    triggeredAt: string
    status: string
    actions: string
    deleteSuccess: string
    updateSuccess: string
    toggleSuccess: string
  }
  
  // 注册
  register: {
    title: string
    emailTab: string
    accountTab: string
    email: string
    emailPlaceholder: string
    verificationCode: string
    verificationCodePlaceholder: string
    sendCode: string
    password: string
    passwordPlaceholder: string
    confirmPassword: string
    confirmPasswordPlaceholder: string
    account: string
    accountPlaceholder: string
    registerButton: string
    haveAccount: string
    loginLink: string
    // 错误消息
    enterEmail: string
    invalidEmail: string
    codeSent: string
    devModeHint: string
    sendCodeFailed: string
    fillAllFields: string
    passwordMismatch: string
    passwordMinLength: string
    codeError: string
    registerSuccess: string
    registerFailed: string
    networkError: string
  }
  
  // 登录
  login: {
    title: string
    account: string
    accountPlaceholder: string
    password: string
    passwordPlaceholder: string
    loginButton: string
    noAccount: string
    signUpLink: string
    enterAccountPassword: string
    loginSuccess: string
    loginFailed: string
    networkError: string
    invalidCredentials: string
    userNotFound: string
  }
}

// 英文翻译
const en: Translations = {
  common: {
    loading: 'Loading',
    confirm: 'Confirm',
    cancel: 'Cancel',
    submit: 'Submit',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    refresh: 'Refresh',
    close: 'Close',
    open: 'Open',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    noData: 'No data',
    completed: 'Completed',
    pending: 'Pending',
    failed: 'Failed',
    retry: 'Retry',
    operationSuccess: 'Operation successful',
    operationFailed: 'Operation failed',
    networkError: 'Network error, please try again later',
    deleteSuccess: 'Deleted successfully'
  },
  navigation: {
    home: 'Home',
    market: 'Market',
    trade: 'Trade',
    portfolio: 'Portfolio',
    mine: 'Profile',
    wallet: 'Wallet'
  },
  home: {
    totalAssets: 'Total Assets',
    available: 'Available',
    trading: 'Trading',
    marketData: 'Market',
    wallet: 'Wallet',
    marketOverview: 'Market Overview',
    secure: 'Secure & Reliable',
    highLiquidity: 'High Liquidity',
    quickDeposit: 'Deposit',
    financialNews: 'Financial News',
  },
  news: {
    title: 'Financial News',
    viewAll: 'View All',
    noNews: 'No news available',
    source: 'Source',
    loadFailed: 'Failed to load news',
    categoryMarket: 'Markets',
    categoryMacro: 'Macro',
    categoryFutures: 'Futures',
    categoryCrypto: 'Crypto',
  },
  trading: {
    trade: 'Trade',
    spotTrading: 'Spot Trading',
    marginTrading: 'Spot Trading',
    secondsTrading: 'Seconds Contract',
    buy: 'Buy',
    sell: 'Sell',
    long: 'Long',
    short: 'Short',
    market: 'Market',
    limit: 'Limit',
    price: 'Price',
    quantity: 'Quantity',
    amount: 'Amount',
    available: 'Available',
    leverage: 'Leverage',
    closePrice: 'Close Price',
    lots: 'Lots',
    percentage: 'Percentage',
    marketValue: 'Market Value',
    margin: 'Margin',
    frozen: 'Frozen',
    pending: 'Pending',
    current: 'Current',
    history: 'History',
    cancelled: 'Cancelled',
    holding: 'Holding',
    closed: 'Closed',
    liquidated: 'Liquidated',
    noHolding: 'No positions',
    noClosedRecords: 'No closed records',
    noCancelledRecords: 'No cancelled records',
    openPrice: 'Open Price',
    profit: 'Profit',
    usStock: 'US Stock',
    hkStock: 'HK Stock',
    crypto: 'Crypto',
    volume24h: '24h Volume',
    high24h: '24h High',
    low24h: '24h Low',
    closePosition: 'Close Position',
    closePositionConfirm: 'Close this position at the current market price?',
    closePositionSuccess: 'Position closed successfully',
    closePositionFailed: 'Failed to close position',
    liquidatedOnClose: 'Position liquidated, margin lost',
    unrealizedProfit: 'Unrealized P/L',
    positionStatus: 'Open',
    openPositionLabel: 'Open',
    crossMargin: 'Cross',
    positionQty: 'Position Qty',
    execClosePrice: 'Exec / Close Price',
    tpSl: 'TP / SL',
    notSet: '--',
    statusLabel: 'Status'
  },
  market: {
    market: 'Market',
    asset: 'Asset',
    price: 'Price',
    change: 'Change',
    volume: 'Volume',
    marketCap: 'Market Cap',
    priceChange: 'Price & Change',
    searchPlaceholder: 'Search assets...',
    loadFailed: 'Failed to load market data',
    tabCrypto: 'Crypto',
    tabFutures: 'Futures',
    tabUsStock: 'US Stocks',
    tabHkStock: 'HK Stocks',
  },
  futures: {
    GC: 'Gold',
    CL: 'WTI Crude Oil',
    BZ: 'Brent Crude',
    SI: 'Silver',
    HG: 'Copper',
    ES: 'E-mini S&P 500',
    NQ: 'E-mini Nasdaq 100',
    YM: 'E-mini Dow',
    RTY: 'E-mini Russell 2000',
    NG: 'Natural Gas',
    '6E': 'Euro FX',
    '6J': 'Japanese Yen',
    '6B': 'British Pound',
    ZN: '10-Year T-Note',
    ZB: '30-Year T-Bond',
  },
  profile: {
    profile: 'Profile',
    walletManagement: 'Wallet Management',
    accountRecords: 'Account Records',
    securityCenter: 'Security Center',
    aboutUs: 'About Us',
    logout: 'Logout',
    recentActivity: 'Recent Activity',
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    securityLevel: 'Security Level',
    verified: 'Verified',
    uid: 'UID',
    walletDesc: 'View assets, deposit, withdraw',
    recordsDesc: 'Deposit, withdraw, trading records',
    securityDesc: 'Bind email, change password',
    aboutDesc: 'Version info, customer support',
    logoutDesc: 'Safely logout your account',
    logoutConfirm: 'Are you sure you want to logout?',
    confirmLogout: 'Confirm Logout',
    noActivity: 'No recent activity',
    securityLevelHigh: 'High'
  },
  security: {
    emailBinding: 'Email Binding',
    notBound: 'Not bound',
    loginPassword: 'Login Password',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    enterCurrentPassword: 'Enter current password',
    enterNewPassword: 'Enter new password',
    enterConfirmPassword: 'Confirm new password',
    passwordMismatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 6 characters',
    passwordChanged: 'Password changed successfully',
    passwordChangeFailed: 'Failed to change password'
  },
  about: {
    contactUs: 'Contact Us',
    noContent: 'Content not available'
  },
  records: {
    withdrawApproved: 'Withdrawal approved',
    withdrawRejected: 'Withdrawal rejected',
    address: 'Address',
    marginClose: 'Margin trade closed',
    marginLiquidated: 'Margin trade liquidated',
    margin: 'Margin',
    profit: 'Profit',
    lostMargin: 'Lost margin',
    adminDeposit: 'Admin deposit'
  },
  auth: {
    login: 'Login',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    loginSuccess: 'Login successful',
    loginFailed: 'Login failed',
    pleaseLogin: 'Please login',
    sessionExpired: 'Your session has expired after 30 minutes of inactivity. Please log in again.'
  },
  customerService: {
    title: 'Customer Service',
    online: 'Online',
    offline: 'Offline',
    welcomeMessage: 'Hello, how can I help you?',
    autoReply: 'Thank you for your inquiry. Our customer service team will assist you shortly.',
    inputPlaceholder: 'Type your message...',
    connecting: 'Connecting...',
    sendMessage: 'Send',
    sendHint: 'Press Enter to send, Shift+Enter for new line',
    initFailed: 'Failed to initialize chat',
    connectFailed: 'Failed to connect to customer service',
    sendFailed: 'Failed to send message',
    sendImageSuccess: 'Image sent successfully',
    sendImageFailed: 'Failed to send image',
    supportAgent: 'Support Agent',
    userLabel: 'User',
    generalInquiry: 'General Inquiry'
  },
  time: {
    minute: 'm',
    hour: 'h',
    day: 'D',
    week: 'W',
    month: 'M',
    year: 'Y'
  },
  kyc: {
    title: 'Identity Verification',
    approved: 'Verified',
    approvedDesc: 'Your identity has been verified',
    reviewing: 'Under Review',
    reviewingDesc: 'Your verification is being reviewed, please wait',
    reviewingTime: 'Usually completed within 1-3 business days',
    rejected: 'Verification Rejected',
    rejectedReason: 'Rejection reason',
    resubmit: 'Please modify and resubmit',
    realName: 'Real Name',
    realNamePlaceholder: 'Enter your real name',
    idNumber: 'ID Number',
    idNumberPlaceholder: 'Enter your ID number',
    idFront: 'ID Front',
    idBack: 'ID Back',
    uploadTip: 'Click to upload',
    submitTip: 'To protect your account security, please submit authentic identity information. Full features will be available after verification.',
    submitTime: 'Submission time',
    refreshStatus: 'Refresh Status',
    fillRequired: 'Please fill in your real name and ID number',
    uploadRequired: 'Please upload both sides of your ID',
    submitSuccess: 'Verification submitted, please wait for review',
    submitFailed: 'Submission failed',
    networkError: 'Network error, please try again later',
    notVerified: 'Not verified, click to submit verification',
    clickToVerify: 'Click to verify',
    waitingReview: 'Under review, please wait',
    rejectedClickResubmit: 'Rejected, click to resubmit'
  },
  wallet: {
    withdrawAmount: 'Withdraw Amount',
    withdrawAmountPlaceholder: 'Enter withdraw amount',
    withdrawAddress: 'Withdraw Address',
    withdrawAddressPlaceholder: 'Enter withdraw address',
    fee: 'Fee',
    actualAmount: 'Actual Amount',
    withdrawing: 'Withdrawing',
    withdrawSuccess: 'Withdrawal request submitted, please wait for review'
  },
  deposit: {
    title: 'Quick Deposit',
    instructions: 'Select a currency, transfer to the address below, then enter the amount and upload your payment screenshot.',
    network: 'Network',
    address: 'Deposit Address',
    copyAddress: 'Copy Address',
    addressCopied: 'Address copied',
    scanQr: 'Scan QR code to transfer',
    amount: 'Deposit Amount',
    amountPlaceholder: 'Enter deposit amount',
    minAmount: 'Minimum deposit',
    screenshot: 'Payment Screenshot',
    uploadScreenshot: 'Upload payment screenshot',
    uploadHint: 'JPG or PNG, max 10MB',
    submit: 'Submit',
    submitSuccess: 'Deposit request submitted, please wait for review',
    submitFailed: 'Submission failed',
    noAddress: 'Deposit address not configured, please contact support',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    history: 'Deposit History',
    selectScreenshot: 'Please upload payment screenshot'
  },
  chart: {
    noData: 'No chart data',
    highest: 'High',
    lowest: 'Low',
    current: 'Current',
    priceChart: 'Price Chart',
    loadFailed: 'Failed to load chart data',
    klineChart: 'Candlestick Chart',
    dataPoints: 'Data points',
    recentData: 'Recent data',
    open: 'Open',
    close: 'Close',
    changeCol: 'Change',
    retry: 'Retry',
    time: 'Time',
    interval1m: '1 min',
    interval5m: '5 min',
    interval15m: '15 min',
    interval1h: '1 hour',
    interval4h: '4 hours',
    interval1d: '1 day'
  },
  order: {
    noOrders: 'No orders',
    quantity: 'Qty'
  },
  seconds: {
    title: 'Seconds Contract',
    direction: 'Direction',
    bullish: 'Bullish',
    bearish: 'Bearish',
    duration: 'Duration',
    investAmount: 'Investment Amount',
    currentPrice: 'Current Price',
    profitRate: 'Profit Rate',
    expectedProfit: 'Expected Profit',
    availableBalance: 'Available Balance',
    confirmBullish: 'Confirm Bullish',
    confirmBearish: 'Confirm Bearish',
    orderSubmitted: 'Order submitted',
    insufficientBalance: 'Insufficient balance',
    orderHistory: 'Order History',
    inProgress: 'In Progress',
    settled: 'Settled',
    win: 'Win',
    lose: 'Lose',
    countdown: 'Countdown',
    openPrice: 'Open Price',
    closePrice: 'Close Price',
    profit: 'Profit',
    noOrders: 'No orders',
    second: 's',
    minute: 'min'
  },
  priceAlert: {
    deleteConfirm: 'Are you sure you want to delete this price alert?',
    selectAsset: 'Select Asset',
    selectAssetPlaceholder: 'Select asset to monitor',
    condition: 'Trigger Condition',
    conditionPlaceholder: 'Select price condition',
    above: 'Price Above',
    below: 'Price Below',
    targetPrice: 'Target Price ($)',
    targetPricePlaceholder: 'Enter target price',
    notificationMethod: 'Notification Method',
    notificationPlaceholder: 'Select notification method',
    emailNotify: 'Email',
    smsNotify: 'SMS',
    pushNotify: 'Push Notification',
    enableStatus: 'Enable Status',
    pageTitle: 'Price Alerts',
    createAlert: 'Create Alert',
    totalCount: 'Total Alerts',
    activeCount: 'Active Alerts',
    triggeredCount: 'Triggered',
    recordsTotal: '{total} records',
    triggeredTag: 'Triggered',
    createdAt: 'Created At',
    triggeredAt: 'Triggered At',
    status: 'Status',
    actions: 'Actions',
    deleteSuccess: 'Alert deleted successfully',
    updateSuccess: 'Alert updated successfully',
    toggleSuccess: 'Alert status updated'
  },
  register: {
    title: 'Create new account',
    emailTab: 'Email',
    accountTab: 'Account',
    email: 'Email',
    emailPlaceholder: 'Enter your email',
    verificationCode: 'Verification Code',
    verificationCodePlaceholder: 'Enter verification code',
    sendCode: 'Send Code',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm your password',
    account: 'Account',
    accountPlaceholder: 'Enter your account',
    registerButton: 'Register',
    haveAccount: 'Already have an account?',
    loginLink: 'Login',
    enterEmail: 'Please enter your email address',
    invalidEmail: 'Please enter a valid email address',
    codeSent: 'Verification code sent',
    devModeHint: 'Dev mode: Check backend console for verification code if email not received',
    sendCodeFailed: 'Failed to send verification code',
    fillAllFields: 'Please fill in all required fields',
    passwordMismatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 6 characters',
    codeError: 'Verification code error',
    registerSuccess: 'Registration successful! Please login',
    registerFailed: 'Registration failed, please try again',
    networkError: 'Network error, please try again later'
  },
  login: {
    title: 'Login to your account',
    account: 'Account',
    accountPlaceholder: 'Enter your account',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    loginButton: 'Login',
    noAccount: "Don't have an account?",
    signUpLink: 'Sign Up',
    enterAccountPassword: 'Please enter account and password',
    loginSuccess: 'Login successful!',
    loginFailed: 'Login failed',
    networkError: 'Network error. Please try again.',
    invalidCredentials: 'Invalid email or password',
    userNotFound: 'User not found'
  }
}

// 中文翻译
const zh: Translations = {
  common: {
    loading: '加载中',
    confirm: '确认',
    cancel: '取消',
    submit: '提交',
    save: '保存',
    delete: '删除',
    edit: '编辑',
    back: '返回',
    next: '下一步',
    previous: '上一步',
    search: '搜索',
    filter: '筛选',
    sort: '排序',
    refresh: '刷新',
    close: '关闭',
    open: '打开',
    yes: '是',
    no: '否',
    all: '全部',
    noData: '暂无数据',
    completed: '已完成',
    pending: '处理中',
    failed: '失败',
    retry: '重试',
    operationSuccess: '操作成功',
    operationFailed: '操作失败',
    networkError: '网络错误，请稍后重试',
    deleteSuccess: '删除成功'
  },
  navigation: {
    home: '首页',
    market: '行情',
    trade: '交易',
    portfolio: '投资组合',
    mine: '我的',
    wallet: '钱包'
  },
  home: {
    totalAssets: '总资产',
    available: '可用',
    trading: '交易',
    marketData: '行情',
    wallet: '钱包',
    marketOverview: '市场概览',
    secure: '安全可靠',
    highLiquidity: '高流动性',
    quickDeposit: '存款',
    financialNews: '财经要闻',
  },
  news: {
    title: '财经新闻',
    viewAll: '查看更多',
    noNews: '暂无新闻',
    source: '来源',
    loadFailed: '加载新闻失败',
    categoryMarket: '市场',
    categoryMacro: '宏观',
    categoryFutures: '期货',
    categoryCrypto: '加密',
  },
  trading: {
    trade: '交易',
    spotTrading: '现货交易',
    marginTrading: '杠杆交易',
    secondsTrading: '秒合约',
    buy: '买入',
    sell: '卖出',
    long: '做多',
    short: '做空',
    market: '市价',
    limit: '限价',
    price: '价格',
    quantity: '数量',
    amount: '金额',
    available: '可用',
    leverage: '杠杆',
    closePrice: '平仓价格',
    lots: '手数',
    percentage: '百分比',
    marketValue: '市值',
    margin: '保证金',
    frozen: '冻结',
    pending: '委托中',
    current: '当前委托',
    history: '已撤单',
    cancelled: '已撤单',
    holding: '持仓中',
    closed: '已平仓',
    liquidated: '已爆仓',
    noHolding: '暂无持仓',
    noClosedRecords: '暂无平仓记录',
    noCancelledRecords: '暂无撤单记录',
    openPrice: '开仓价',
    profit: '收益',
    usStock: '美股',
    hkStock: '港股',
    crypto: '加密货币',
    volume24h: '24h成交量',
    high24h: '24h最高',
    low24h: '24h最低',
    closePosition: '平仓',
    closePositionConfirm: '确认以当前市价平仓该持仓？',
    closePositionSuccess: '平仓成功',
    closePositionFailed: '平仓失败',
    liquidatedOnClose: '已爆仓，保证金已全部损失',
    unrealizedProfit: '浮动盈亏',
    positionStatus: '持仓',
    openPositionLabel: '开仓',
    crossMargin: '全仓',
    positionQty: '持仓数量',
    execClosePrice: '成交价/平仓价格',
    tpSl: '止盈/止损',
    notSet: '--',
    statusLabel: '状态'
  },
  market: {
    market: '市场',
    asset: '资产',
    price: '价格',
    change: '涨跌',
    volume: '成交量',
    marketCap: '市值',
    priceChange: '价格涨跌',
    searchPlaceholder: '搜索资产...',
    loadFailed: '加载市场数据失败',
    tabCrypto: '加密货币',
    tabFutures: '国际期货',
    tabUsStock: '美股',
    tabHkStock: '港股',
  },
  futures: {
    GC: '国际黄金',
    CL: 'WTI原油',
    BZ: '布伦特原油',
    SI: '国际白银',
    HG: '铜',
    ES: '标普500迷你',
    NQ: '纳指100迷你',
    YM: '道琼斯迷你',
    RTY: '罗素2000迷你',
    NG: '天然气',
    '6E': '欧元兑美元',
    '6J': '日元兑美元',
    '6B': '英镑兑美元',
    ZN: '美国10年期国债',
    ZB: '美国30年期国债',
  },
  profile: {
    profile: '我的',
    walletManagement: '钱包管理',
    accountRecords: '账户记录',
    securityCenter: '安全中心',
    aboutUs: '关于我们',
    logout: '退出登录',
    recentActivity: '最近活动',
    deposit: '充值',
    withdraw: '提现',
    securityLevel: '安全等级',
    verified: '已认证',
    uid: 'UID',
    walletDesc: '查看资产、充值、提现',
    recordsDesc: '充值、提现、交易记录',
    securityDesc: '绑定邮箱、修改密码',
    aboutDesc: '版本信息、客户支持',
    logoutDesc: '安全退出您的账户',
    logoutConfirm: '确定要退出登录吗？',
    confirmLogout: '确认退出',
    noActivity: '暂无活动记录',
    securityLevelHigh: '高'
  },
  security: {
    emailBinding: '邮箱绑定',
    notBound: '未绑定',
    loginPassword: '登录密码',
    changePassword: '修改密码',
    currentPassword: '当前密码',
    newPassword: '新密码',
    confirmNewPassword: '确认新密码',
    enterCurrentPassword: '请输入当前密码',
    enterNewPassword: '请输入新密码',
    enterConfirmPassword: '请确认新密码',
    passwordMismatch: '两次输入的密码不一致',
    passwordMinLength: '密码长度至少6位',
    passwordChanged: '密码修改成功',
    passwordChangeFailed: '密码修改失败'
  },
  about: {
    contactUs: '联系我们',
    noContent: '暂无内容'
  },
  records: {
    withdrawApproved: '提现已通过审核',
    withdrawRejected: '提现被驳回',
    address: '地址',
    marginClose: '杠杆交易平仓',
    marginLiquidated: '杠杆交易爆仓',
    margin: '保证金',
    profit: '收益',
    lostMargin: '损失保证金',
    adminDeposit: '管理员上分'
  },
  auth: {
    login: '登录',
    logout: '退出',
    email: '邮箱',
    password: '密码',
    loginSuccess: '登录成功',
    loginFailed: '登录失败',
    pleaseLogin: '请先登录',
    sessionExpired: '登录已超时（30分钟无操作），请重新登录'
  },
  customerService: {
    title: '客服',
    online: '在线',
    offline: '离线',
    welcomeMessage: '您好，请问有什么可以帮您？',
    autoReply: '感谢您的咨询，我们的客服人员会尽快为您处理。',
    inputPlaceholder: '输入您的问题...',
    connecting: '连接中...',
    sendMessage: '发送',
    sendHint: '按 Enter 发送，Shift+Enter 换行',
    initFailed: '初始化聊天失败',
    connectFailed: '连接客服失败',
    sendFailed: '发送消息失败',
    sendImageSuccess: '图片发送成功',
    sendImageFailed: '图片发送失败',
    supportAgent: '客服',
    userLabel: '用户',
    generalInquiry: '一般咨询'
  },
  time: {
    minute: '分',
    hour: '时',
    day: '日',
    week: '周',
    month: '月',
    year: '年'
  },
  kyc: {
    title: '身份认证',
    approved: '认证已通过',
    approvedDesc: '您的身份认证已通过审核',
    reviewing: '审核中',
    reviewingDesc: '您的身份认证资料正在审核中，请耐心等待',
    reviewingTime: '通常在1-3个工作日内完成审核',
    rejected: '认证被拒绝',
    rejectedReason: '拒绝原因',
    resubmit: '请修改后重新提交',
    realName: '真实姓名',
    realNamePlaceholder: '请输入真实姓名',
    idNumber: '证件号码',
    idNumberPlaceholder: '请输入身份证号码',
    idFront: '身份证正面',
    idBack: '身份证反面',
    uploadTip: '点击上传',
    submitTip: '为了保障您的账户安全，请提交真实的身份信息。审核通过后即可使用全部功能。',
    submitTime: '提交时间',
    refreshStatus: '刷新状态',
    fillRequired: '请填写真实姓名和证件号码',
    uploadRequired: '请上传身份证正反面照片',
    submitSuccess: '身份认证资料已提交，请等待管理员审核',
    submitFailed: '提交失败',
    networkError: '网络错误，请稍后重试',
    notVerified: '未认证，点击提交认证资料',
    clickToVerify: '点击认证',
    waitingReview: '审核中，请等待管理员审核',
    rejectedClickResubmit: '认证被拒绝，点击重新提交'
  },
  wallet: {
    withdrawAmount: '提现金额',
    withdrawAmountPlaceholder: '请输入提现金额',
    withdrawAddress: '提现地址',
    withdrawAddressPlaceholder: '请输入提现地址',
    fee: '手续费',
    actualAmount: '实际到账',
    withdrawing: '提现中',
    withdrawSuccess: '提现申请已提交，请等待审核'
  },
  deposit: {
    title: '快捷充币',
    instructions: '请选择币种，向对应地址转账，然后填写充值金额并上传转账成功截图。',
    network: '网络',
    address: '收款地址',
    copyAddress: '复制地址',
    addressCopied: '地址已复制',
    scanQr: '扫描二维码转账',
    amount: '充值金额',
    amountPlaceholder: '请输入充值金额',
    minAmount: '最低充值',
    screenshot: '转账截图',
    uploadScreenshot: '上传转账成功截图',
    uploadHint: '支持 JPG、PNG 格式，最大 10MB',
    submit: '确认提交',
    submitSuccess: '充值申请已提交，请等待审核',
    submitFailed: '提交失败',
    noAddress: '暂未配置收款地址，请联系客服',
    pending: '审核中',
    approved: '已通过',
    rejected: '已驳回',
    history: '充值记录',
    selectScreenshot: '请上传转账截图'
  },
  chart: {
    noData: '暂无图表数据',
    highest: '最高',
    lowest: '最低',
    current: '当前',
    priceChart: '价格走势',
    loadFailed: '加载图表数据失败',
    klineChart: 'K线图',
    dataPoints: '数据点',
    recentData: '最近数据',
    open: '开盘',
    close: '收盘',
    changeCol: '涨跌',
    retry: '重试',
    time: '时间',
    interval1m: '1分钟',
    interval5m: '5分钟',
    interval15m: '15分钟',
    interval1h: '1小时',
    interval4h: '4小时',
    interval1d: '1天'
  },
  order: {
    noOrders: '暂无订单记录',
    quantity: '数量'
  },
  seconds: {
    title: '秒合约',
    direction: '方向',
    bullish: '看涨',
    bearish: '看跌',
    duration: '合约时长',
    investAmount: '投资金额',
    currentPrice: '当前价格',
    profitRate: '收益率',
    expectedProfit: '预计收益',
    availableBalance: '可用余额',
    confirmBullish: '确认看涨',
    confirmBearish: '确认看跌',
    orderSubmitted: '订单已提交',
    insufficientBalance: '余额不足',
    orderHistory: '订单记录',
    inProgress: '进行中',
    settled: '已结算',
    win: '盈利',
    lose: '亏损',
    countdown: '倒计时',
    openPrice: '开仓价',
    closePrice: '平仓价',
    profit: '收益',
    noOrders: '暂无订单',
    second: '秒',
    minute: '分钟'
  },
  priceAlert: {
    deleteConfirm: '确定要删除这个价格提醒吗？',
    selectAsset: '选择资产',
    selectAssetPlaceholder: '选择要监控的资产',
    condition: '触发条件',
    conditionPlaceholder: '选择价格条件',
    above: '价格高于',
    below: '价格低于',
    targetPrice: '目标价格 ($)',
    targetPricePlaceholder: '输入目标价格',
    notificationMethod: '通知方式',
    notificationPlaceholder: '选择通知方式',
    emailNotify: '邮件通知',
    smsNotify: '短信通知',
    pushNotify: '推送通知',
    enableStatus: '启用状态',
    pageTitle: '价格提醒',
    createAlert: '创建提醒',
    totalCount: '总提醒数',
    activeCount: '活跃提醒',
    triggeredCount: '已触发',
    recordsTotal: '共 {total} 条记录',
    triggeredTag: '已触发',
    createdAt: '创建时间',
    triggeredAt: '触发时间',
    status: '状态',
    actions: '操作',
    deleteSuccess: '提醒已删除',
    updateSuccess: '提醒已更新',
    toggleSuccess: '提醒状态已更新'
  },
  register: {
    title: '创建新账户',
    emailTab: '邮箱注册',
    accountTab: '账号注册',
    email: '邮箱',
    emailPlaceholder: '请输入邮箱地址',
    verificationCode: '验证码',
    verificationCodePlaceholder: '请输入验证码',
    sendCode: '发送验证码',
    password: '密码',
    passwordPlaceholder: '请输入密码',
    confirmPassword: '确认密码',
    confirmPasswordPlaceholder: '请再次输入密码',
    account: '账号',
    accountPlaceholder: '请输入账号',
    registerButton: '注册',
    haveAccount: '已有账号？',
    loginLink: '立即登录',
    enterEmail: '请输入邮箱地址',
    invalidEmail: '请输入有效的邮箱地址',
    codeSent: '验证码已发送',
    devModeHint: '开发模式：如果收不到邮件，请查看后端控制台获取验证码',
    sendCodeFailed: '发送验证码失败',
    fillAllFields: '请填写所有必填项',
    passwordMismatch: '两次输入的密码不一致',
    passwordMinLength: '密码至少6位',
    codeError: '验证码错误',
    registerSuccess: '注册成功！请登录',
    registerFailed: '注册失败，请重试',
    networkError: '网络错误，请稍后重试'
  },
  login: {
    title: '登录您的账户',
    account: '账号',
    accountPlaceholder: '请输入账号',
    password: '密码',
    passwordPlaceholder: '请输入密码',
    loginButton: '登录',
    noAccount: '还没有账号？',
    signUpLink: '立即注册',
    enterAccountPassword: '请输入账号和密码',
    loginSuccess: '登录成功！',
    loginFailed: '登录失败',
    networkError: '网络错误，请稍后重试',
    invalidCredentials: '邮箱或密码错误',
    userNotFound: '用户不存在'
  }
}

// 日文翻译
const ja: Translations = {
  common: {
    loading: '読み込み中',
    confirm: '確認',
    cancel: 'キャンセル',
    submit: '送信',
    save: '保存',
    delete: '削除',
    edit: '編集',
    back: '戻る',
    next: '次へ',
    previous: '前へ',
    search: '検索',
    filter: 'フィルター',
    sort: 'ソート',
    refresh: '更新',
    close: '閉じる',
    open: '開く',
    yes: 'はい',
    no: 'いいえ',
    all: 'すべて',
    noData: 'データなし',
    completed: '完了',
    pending: '処理中',
    failed: '失敗',
    retry: '重試',
    operationSuccess: '操作が成功しました',
    operationFailed: '操作に失敗しました',
    networkError: 'ネットワークエラー、後でもう一度お試しください',
    deleteSuccess: '削除しました'
  },
  navigation: {
    home: 'ホーム',
    market: 'マーケット',
    trade: 'トレード',
    portfolio: 'ポートフォリオ',
    mine: 'マイページ',
    wallet: 'ウォレット'
  },
  home: {
    totalAssets: '総資産',
    available: '利用可能',
    trading: 'トレード',
    marketData: 'マーケット',
    wallet: 'ウォレット',
    marketOverview: 'マーケット概要',
    secure: '安全・信頼',
    highLiquidity: '高流動性',
    quickDeposit: '入金',
    financialNews: '金融ニュース',
  },
  news: {
    title: '金融ニュース',
    viewAll: 'すべて見る',
    noNews: 'ニュースがありません',
    source: '出典',
    loadFailed: 'ニュースの読み込みに失敗しました',
    categoryMarket: 'マーケット',
    categoryMacro: 'マクロ',
    categoryFutures: '先物',
    categoryCrypto: '暗号資産',
  },
  trading: {
    trade: 'トレード',
    spotTrading: 'スポット取引',
    marginTrading: 'マージン取引',
    secondsTrading: '秒契約',
    buy: '買い',
    sell: '売り',
    long: 'ロング',
    short: 'ショート',
    market: '成行',
    limit: '指値',
    price: '価格',
    quantity: '数量',
    amount: '金額',
    available: '利用可能',
    leverage: 'レバレッジ',
    closePrice: '決済価格',
    lots: 'ロット',
    percentage: 'パーセント',
    marketValue: '時価総額',
    margin: '証拠金',
    frozen: '凍結',
    pending: '注文中',
    current: '現在の注文',
    history: 'キャンセル済み',
    cancelled: 'キャンセル済み',
    holding: 'ポジション',
    closed: '決済済み',
    liquidated: '強制決済',
    noHolding: 'ポジションなし',
    noClosedRecords: '決済履歴なし',
    noCancelledRecords: 'キャンセル履歴なし',
    openPrice: '建値',
    profit: '損益',
    usStock: '米国株',
    hkStock: '香港株',
    crypto: '暗号資産',
    volume24h: '24h出来高',
    high24h: '24h高値',
    low24h: '24h安値',
    closePosition: '決済',
    closePositionConfirm: '現在の成行価格で決済しますか？',
    closePositionSuccess: '決済が完了しました',
    closePositionFailed: '決済に失敗しました',
    liquidatedOnClose: '強制決済され、証拠金を失いました',
    unrealizedProfit: '評価損益',
    positionStatus: 'ポジション',
    openPositionLabel: '建玉',
    crossMargin: 'クロス',
    positionQty: '保有数量',
    execClosePrice: '約定/決済価格',
    tpSl: '利確/損切',
    notSet: '--',
    statusLabel: '状態'
  },
  market: {
    market: 'マーケット',
    asset: '資産',
    price: '価格',
    change: '変動',
    volume: '出来高',
    marketCap: '時価総額',
    priceChange: '価格変動',
    searchPlaceholder: '資産を検索...',
    loadFailed: 'マーケットデータの読み込みに失敗しました',
    tabCrypto: '暗号資産',
    tabFutures: '国際先物',
    tabUsStock: '米国株',
    tabHkStock: '香港株',
  },
  futures: {
    GC: '金（ゴールド）',
    CL: 'WTI原油',
    BZ: 'ブレント原油',
    SI: '銀',
    HG: '銅',
    ES: 'S&P500ミニ',
    NQ: 'ナスダック100ミニ',
    YM: 'ダウミニ',
    RTY: 'ラッセル2000ミニ',
    NG: '天然ガス',
    '6E': 'ユーロ/ドル',
    '6J': '円/ドル',
    '6B': 'ポンド/ドル',
    ZN: '米10年国債',
    ZB: '米30年国債',
  },
  profile: {
    profile: 'マイページ',
    walletManagement: 'ウォレット管理',
    accountRecords: 'アカウント記録',
    securityCenter: 'セキュリティセンター',
    aboutUs: '私たちについて',
    logout: 'ログアウト',
    recentActivity: '最近のアクティビティ',
    deposit: '入金',
    withdraw: '出金',
    securityLevel: 'セキュリティレベル',
    verified: '認証済み',
    uid: 'UID',
    walletDesc: '資産確認、入金、出金',
    recordsDesc: '入金、出金、取引記録',
    securityDesc: 'メール連携、パスワード変更',
    aboutDesc: 'バージョン情報、カスタマーサポート',
    logoutDesc: 'アカウントから安全にログアウト',
    logoutConfirm: 'ログアウトしてもよろしいですか？',
    confirmLogout: 'ログアウト確認',
    noActivity: '最近のアクティビティはありません',
    securityLevelHigh: '高い'
  },
  security: {
    emailBinding: 'メール連携',
    notBound: '未連携',
    loginPassword: 'ログインパスワード',
    changePassword: 'パスワード変更',
    currentPassword: '現在のパスワード',
    newPassword: '新しいパスワード',
    confirmNewPassword: '新しいパスワードの確認',
    enterCurrentPassword: '現在のパスワードを入力',
    enterNewPassword: '新しいパスワードを入力',
    enterConfirmPassword: '新しいパスワードを確認',
    passwordMismatch: 'パスワードが一致しません',
    passwordMinLength: 'パスワードは6文字以上必要です',
    passwordChanged: 'パスワードが変更されました',
    passwordChangeFailed: 'パスワードの変更に失敗しました'
  },
  about: {
    contactUs: 'お問い合わせ',
    noContent: 'コンテンツがありません'
  },
  records: {
    withdrawApproved: '出金が承認されました',
    withdrawRejected: '出金が拒否されました',
    address: 'アドレス',
    marginClose: 'レバレッジ取引決済',
    marginLiquidated: 'レバレッジ取引清算',
    margin: '証拠金',
    profit: '利益',
    lostMargin: '証拠金損失',
    adminDeposit: '管理者入金'
  },
  auth: {
    login: 'ログイン',
    logout: 'ログアウト',
    email: 'メール',
    password: 'パスワード',
    loginSuccess: 'ログイン成功',
    loginFailed: 'ログイン失敗',
    pleaseLogin: 'ログインしてください',
    sessionExpired: '30分間操作がなかったため、ログインがタイムアウトしました。再度ログインしてください'
  },
  customerService: {
    title: 'カスタマーサービス',
    online: 'オンライン',
    offline: 'オフライン',
    welcomeMessage: 'こんにちは、何かお手伝いできることはありますか？',
    autoReply: 'お問い合わせありがとうございます。カスタマーサービスチームがすぐにサポートいたします。',
    inputPlaceholder: 'メッセージを入力...',
    connecting: '接続中...',
    sendMessage: '送信',
    sendHint: 'Enter で送信、Shift+Enter で改行',
    initFailed: 'チャットの初期化に失敗しました',
    connectFailed: 'カスタマーサービスへの接続に失敗しました',
    sendFailed: 'メッセージの送信に失敗しました',
    sendImageSuccess: '画像を送信しました',
    sendImageFailed: '画像の送信に失敗しました',
    supportAgent: 'サポート担当',
    userLabel: 'ユーザー',
    generalInquiry: '一般的なお問い合わせ'
  },
  time: {
    minute: '分',
    hour: '時',
    day: '日',
    week: '週',
    month: '月',
    year: '年'
  },
  kyc: {
    title: '本人確認',
    approved: '認証完了',
    approvedDesc: '本人確認が完了しました',
    reviewing: '審査中',
    reviewingDesc: '本人確認書類を審査中です。しばらくお待ちください',
    reviewingTime: '通常1〜3営業日以内に完了します',
    rejected: '認証拒否',
    rejectedReason: '拒否理由',
    resubmit: '修正して再提出してください',
    realName: '本名',
    realNamePlaceholder: '本名を入力してください',
    idNumber: '身分証明書番号',
    idNumberPlaceholder: '身分証明書番号を入力してください',
    idFront: '身分証明書（表面）',
    idBack: '身分証明書（裏面）',
    uploadTip: 'クリックしてアップロード',
    submitTip: 'アカウントのセキュリティのため、正確な本人情報を提出してください。認証後、すべての機能が利用可能になります。',
    submitTime: '提出日時',
    refreshStatus: 'ステータス更新',
    fillRequired: '本名と身分証明書番号を入力してください',
    uploadRequired: '身分証明書の両面をアップロードしてください',
    submitSuccess: '本人確認書類を提出しました。審査をお待ちください',
    submitFailed: '提出に失敗しました',
    networkError: 'ネットワークエラー、後でもう一度お試しください',
    notVerified: '未認証、クリックして認証資料を提出',
    clickToVerify: 'クリックして認証',
    waitingReview: '審査中、管理者の審査をお待ちください',
    rejectedClickResubmit: '認証拒否、クリックして再提出'
  },
  wallet: {
    withdrawAmount: '出金額',
    withdrawAmountPlaceholder: '出金額を入力してください',
    withdrawAddress: '出金アドレス',
    withdrawAddressPlaceholder: '出金アドレスを入力してください',
    fee: '手数料',
    actualAmount: '実際の受取額',
    withdrawing: '出金処理中',
    withdrawSuccess: '出金申請を送信しました。審査をお待ちください'
  },
  deposit: {
    title: 'クイック入金',
    instructions: '通貨を選択し、該当アドレスへ送金後、金額を入力して送金スクリーンショットをアップロードしてください。',
    network: 'ネットワーク',
    address: '入金アドレス',
    copyAddress: 'アドレスをコピー',
    addressCopied: 'アドレスをコピーしました',
    scanQr: 'QRコードで送金',
    amount: '入金金額',
    amountPlaceholder: '入金金額を入力',
    minAmount: '最低入金額',
    screenshot: '送金スクリーンショット',
    uploadScreenshot: '送金成功スクリーンショットをアップロード',
    uploadHint: 'JPG/PNG、最大10MB',
    submit: '送信',
    submitSuccess: '入金申請を送信しました。審査をお待ちください',
    submitFailed: '送信に失敗しました',
    noAddress: '入金アドレスが設定されていません。サポートにお問い合わせください',
    pending: '審査中',
    approved: '承認済み',
    rejected: '却下',
    history: '入金履歴',
    selectScreenshot: '送金スクリーンショットをアップロードしてください'
  },
  chart: {
    noData: 'チャートデータがありません',
    highest: '高値',
    lowest: '安値',
    current: '現在',
    priceChart: '価格チャート',
    loadFailed: 'チャートデータの読み込みに失敗しました',
    klineChart: 'ローソク足チャート',
    dataPoints: 'データポイント',
    recentData: '最近のデータ',
    open: '始値',
    close: '終値',
    changeCol: '変動',
    retry: '再試行',
    time: '時間',
    interval1m: '1分',
    interval5m: '5分',
    interval15m: '15分',
    interval1h: '1時間',
    interval4h: '4時間',
    interval1d: '1日'
  },
  order: {
    noOrders: '注文履歴がありません',
    quantity: '数量'
  },
  seconds: {
    title: '秒契約',
    direction: '方向',
    bullish: '上昇',
    bearish: '下落',
    duration: '契約時間',
    investAmount: '投資金額',
    currentPrice: '現在価格',
    profitRate: '収益率',
    expectedProfit: '予想収益',
    availableBalance: '利用可能残高',
    confirmBullish: '上昇を確認',
    confirmBearish: '下落を確認',
    orderSubmitted: '注文が送信されました',
    insufficientBalance: '残高不足',
    orderHistory: '注文履歴',
    inProgress: '進行中',
    settled: '決済済み',
    win: '勝利',
    lose: '損失',
    countdown: 'カウントダウン',
    openPrice: '開始価格',
    closePrice: '終了価格',
    profit: '収益',
    noOrders: '注文なし',
    second: '秒',
    minute: '分'
  },
  priceAlert: {
    deleteConfirm: 'この価格アラートを削除してもよろしいですか？',
    selectAsset: '資産を選択',
    selectAssetPlaceholder: '監視する資産を選択',
    condition: 'トリガー条件',
    conditionPlaceholder: '価格条件を選択',
    above: '価格が上回る',
    below: '価格が下回る',
    targetPrice: '目標価格 ($)',
    targetPricePlaceholder: '目標価格を入力',
    notificationMethod: '通知方法',
    notificationPlaceholder: '通知方法を選択',
    emailNotify: 'メール通知',
    smsNotify: 'SMS通知',
    pushNotify: 'プッシュ通知',
    enableStatus: '有効状態',
    pageTitle: '価格アラート',
    createAlert: 'アラート作成',
    totalCount: '総アラート数',
    activeCount: '有効アラート',
    triggeredCount: '発動済み',
    recordsTotal: '合計 {total} 件',
    triggeredTag: '発動済み',
    createdAt: '作成日時',
    triggeredAt: '発動日時',
    status: 'ステータス',
    actions: '操作',
    deleteSuccess: 'アラートを削除しました',
    updateSuccess: 'アラートを更新しました',
    toggleSuccess: 'アラートの状態を更新しました'
  },
  register: {
    title: '新規アカウント作成',
    emailTab: 'メール登録',
    accountTab: 'アカウント登録',
    email: 'メールアドレス',
    emailPlaceholder: 'メールアドレスを入力',
    verificationCode: '認証コード',
    verificationCodePlaceholder: '認証コードを入力',
    sendCode: 'コード送信',
    password: 'パスワード',
    passwordPlaceholder: 'パスワードを入力',
    confirmPassword: 'パスワード確認',
    confirmPasswordPlaceholder: 'パスワードを再入力',
    account: 'アカウント',
    accountPlaceholder: 'アカウントを入力',
    registerButton: '登録',
    haveAccount: 'アカウントをお持ちですか？',
    loginLink: 'ログイン',
    enterEmail: 'メールアドレスを入力してください',
    invalidEmail: '有効なメールアドレスを入力してください',
    codeSent: '認証コードを送信しました',
    devModeHint: '開発モード：メールが届かない場合は、バックエンドコンソールで認証コードを確認してください',
    sendCodeFailed: '認証コードの送信に失敗しました',
    fillAllFields: 'すべての必須項目を入力してください',
    passwordMismatch: 'パスワードが一致しません',
    passwordMinLength: 'パスワードは6文字以上必要です',
    codeError: '認証コードエラー',
    registerSuccess: '登録成功！ログインしてください',
    registerFailed: '登録に失敗しました。もう一度お試しください',
    networkError: 'ネットワークエラー、後でもう一度お試しください'
  },
  login: {
    title: 'アカウントにログイン',
    account: 'アカウント',
    accountPlaceholder: 'アカウントを入力',
    password: 'パスワード',
    passwordPlaceholder: 'パスワードを入力',
    loginButton: 'ログイン',
    noAccount: 'アカウントをお持ちでないですか？',
    signUpLink: '新規登録',
    enterAccountPassword: 'アカウントとパスワードを入力してください',
    loginSuccess: 'ログイン成功！',
    loginFailed: 'ログインに失敗しました',
    networkError: 'ネットワークエラー、後でもう一度お試しください',
    invalidCredentials: 'メールアドレスまたはパスワードが正しくありません',
    userNotFound: 'ユーザーが見つかりません'
  }
}

// 翻译映射
// 由简体自动生成完整繁体语言包
const zhTW = deepToTraditional(zh) as Translations

function flattenTranslations(obj: unknown, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {}
  if (!obj || typeof obj !== 'object') return result
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'string') {
      result[path] = value
    } else if (value && typeof value === 'object') {
      Object.assign(result, flattenTranslations(value, path))
    }
  }
  return result
}

// 预计算所有 zh-TW 词条，确保全覆盖
const zhTWFlatMap: Record<string, string> = (() => {
  const flat = flattenTranslations(zh)
  const converted: Record<string, string> = {}
  for (const [key, value] of Object.entries(flat)) {
    converted[key] = toTraditional(value)
  }
  for (const [key, value] of Object.entries(ZH_TW_OVERRIDES)) {
    converted[key] = value
  }
  return converted
})()

export const translations: Record<Language, Translations> = {
  en,
  zh,
  'zh-TW': zhTW,
  ja
}

// 根据语言获取日期/数字 locale
export const getLocaleForLanguage = (language: Language): string => {
  switch (language) {
    case 'zh': return 'zh-CN'
    case 'zh-TW': return 'zh-TW'
    case 'ja': return 'ja-JP'
    default: return 'en-US'
  }
}

// 获取 API 内容语言参数
export const getApiLanguage = (language: Language): string => {
  if (language === 'zh-TW') return 'zh'
  return language
}

// 获取翻译文本的函数
export const getTranslation = (language: Language, key: string): string => {
  if (language === 'zh-TW' && zhTWFlatMap[key]) {
    return zhTWFlatMap[key]
  }

  const keys = key.split('.')
  let value: any = translations[language]

  for (const k of keys) {
    value = value?.[k]
  }

  if (typeof value === 'string') return value

  return key
}

// 运行时文本转换（后端返回的简体中文等）
export const translateText = (language: Language, text: string): string => {
  if (language === 'zh-TW' && text) {
    return toTraditional(text)
  }
  return text
}

// 默认语言
export const DEFAULT_LANGUAGE: Language = 'en'